<style lang="less" scoped>
.base-multi-input {
  position: relative;
  border: 1px solid var(--border, #e8eaec);
  border-radius: 10px;
  background: var(--bg2, #fff);
  overflow: hidden;
  transition: border-color 0.2s, box-shadow 0.2s;

  &.is-dragover {
    border-color: #2d8cf0;
    box-shadow: 0 0 0 3px rgba(45, 140, 240, 0.18);

    .drag-overlay {
      opacity: 1;
      pointer-events: none;
    }
  }

  &__textarea {
    /deep/ textarea.ivu-input {
      border: none;
      border-radius: 0;
      resize: vertical;
      min-height: 120px;
      box-shadow: none;
      background: transparent;
    }
  }

  &__toolbar {
    display: flex;
    align-items: center;
    padding: 6px 10px;
    border-top: 1px dashed var(--border, #e8eaec);
    background: linear-gradient(to bottom, transparent, rgba(45, 140, 240, 0.04));
  }

  &__attach {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: 1px solid var(--border, #dcdee2);
    border-radius: 8px;
    background: #fff;
    color: #2d8cf0;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      border-color: #2d8cf0;
      background: #f0f7ff;
      transform: translateY(-1px);
    }

    &.is-disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
    }
  }

  &__hint {
    margin-left: 10px;
    font-size: 12px;
    color: var(--subtext2, #999);
  }

  &__files {
    padding: 0 10px 10px;
  }

  &__file {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 8px;
    margin-top: 6px;
    border-radius: 8px;
    background: var(--bg3, #f7f8fa);
    font-size: 13px;

    &-name {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &-remove {
      color: #ed4014;
      cursor: pointer;
      padding: 2px 6px;
      border-radius: 4px;

      &:hover {
        background: rgba(237, 64, 20, 0.1);
      }
    }
  }

  .drag-overlay {
    position: absolute;
    inset: 0;
    z-index: 5;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(45, 140, 240, 0.12);
    backdrop-filter: blur(2px);
    opacity: 0;
    transition: opacity 0.2s;
    pointer-events: none;

    &__inner {
      padding: 16px 28px;
      border: 2px dashed #2d8cf0;
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.92);
      color: #2d8cf0;
      font-weight: 600;
      box-shadow: 0 8px 24px rgba(45, 140, 240, 0.15);
      animation: multi-input-pulse 1.2s ease-in-out infinite;
    }
  }
}

@keyframes multi-input-pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.02); }
}
</style>

<template>
  <div
    class="base-multi-input"
    :class="{ 'is-dragover': dragOver }"
    @dragenter.prevent="onDragEnter"
    @dragover.prevent="onDragOver"
    @dragleave.prevent="onDragLeave"
    @drop.prevent="onDrop"
  >
    <div class="drag-overlay">
      <div class="drag-overlay__inner">
        <Icon type="ios-cloud-upload" size="28" style="margin-right:8px;" />
        释放文件以上传
      </div>
    </div>

    <Input
      class="base-multi-input__textarea"
      type="textarea"
      :value="localText"
      :rows="rows"
      :placeholder="placeholder"
      :disabled="disabled || uploading"
      @input="onTextInput"
    />

    <div class="base-multi-input__toolbar">
      <div
        class="base-multi-input__attach"
        :class="{ 'is-disabled': disabled || uploading }"
        title="添加附件"
        @click="pickFiles"
      >
        <Icon type="ios-attach" size="20" />
      </div>
      <span class="base-multi-input__hint">支持拖拽多个文件到输入区</span>
      <input
        ref="fileInput"
        type="file"
        multiple
        :accept="accept"
        style="display:none"
        @change="onFilePick"
      />
    </div>

    <div v-if="displayFiles.length" class="base-multi-input__files">
      <div
        v-for="(f, i) in displayFiles"
        :key="f.file_id || f.id || i"
        class="base-multi-input__file"
      >
        <Icon :type="fileIcon(f)" size="16" />
        <span class="base-multi-input__file-name" :title="f.name">{{ f.name }}</span>
        <span v-if="f.loading" style="font-size:12px;color:#2d8cf0;">{{ f.percent || '上传中' }}</span>
        <span
          v-else
          class="base-multi-input__file-remove"
          @click="removeFile(i)"
        >移除</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BaseMultiInput',
  props: {
    value: {
      type: Object,
      default: () => ({text: '', files: []}),
    },
    placeholder: {
      type: String,
      default: '请输入内容，可拖拽或点击附件按钮上传文件…',
    },
    rows: {
      type: Number,
      default: 6,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    accept: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      localText: '',
      savedFiles: [],
      pendingFiles: [],
      dragOver: false,
      dragDepth: 0,
      uploading: false,
    };
  },
  computed: {
    displayFiles() {
      return [...this.savedFiles, ...this.pendingFiles];
    },
  },
  watch: {
    value: {
      handler(v) {
        if (!v) {
          return;
        }
        if (v.text !== this.localText) {
          this.localText = v.text || '';
        }
        if (Array.isArray(v.files) && v.files.length) {
          this.hydrateFiles(v.files);
        }
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    fileIcon(f) {
      const ext = (f.ext || '').toLowerCase();
      if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
        return 'ios-image';
      }
      if (['pdf'].includes(ext)) {
        return 'ios-document';
      }
      return 'ios-document-outline';
    },
    onTextInput(v) {
      this.localText = v;
      this.emitChange();
    },
    emitChange() {
      this.$emit('input', {
        text: this.localText,
        files: this.savedFiles.map((f) => ({
          file_id: f.id || f.file_id,
          name: f.name,
          url: f.url,
          type: f.type,
          ext: f.ext,
        })),
      });
    },
    pickFiles() {
      if (this.disabled || this.uploading) {
        return;
      }
      this.$refs.fileInput.click();
    },
    onFilePick(e) {
      const files = Array.from(e.target.files || []);
      e.target.value = '';
      if (files.length) {
        this.uploadFiles(files);
      }
    },
    onDragEnter() {
      this.dragDepth += 1;
      this.dragOver = true;
    },
    onDragOver() {
      this.dragOver = true;
    },
    onDragLeave() {
      this.dragDepth -= 1;
      if (this.dragDepth <= 0) {
        this.dragDepth = 0;
        this.dragOver = false;
      }
    },
    onDrop(e) {
      this.dragDepth = 0;
      this.dragOver = false;
      const files = Array.from(e.dataTransfer.files || []);
      if (files.length) {
        this.uploadFiles(files);
      }
    },
    uploadFiles(rawFiles) {
      if (!this.$cos || !this.$cos.upload) {
        this.Error('上传服务未就绪');
        return;
      }
      this.uploading = true;
      const coskey = this.$store.getters['session/session'].coskey;
      this.$cos.upload(
          rawFiles,
          {
            onPreload: (files) => {
              this.pendingFiles = files.map((f) => Object.assign({}, f, {loading: true}));
            },
            onFail: (err) => {
              this.Error('上传失败:' + err);
              this.pendingFiles = [];
              this.uploading = false;
            },
            onProgress: (file) => {
              this.$set(file, 'percent', file.percent + '%');
            },
            onSuccessFile: (file, index) => {
              this.pendingFiles.splice(index, 1, Object.assign({}, file, {loading: false}));
            },
            onSuccess: (files) => {
              const uploaded = Array.isArray(files) && files.length ?
                files :
                this.pendingFiles.filter((f) => !f.loading && (f.url || f.id));
              this.savedFiles = this.savedFiles.concat(uploaded);
              this.pendingFiles = [];
              this.uploading = false;
              this.emitChange();
            },
          },
          {coskey},
      );
    },
    hydrateFiles(fileRefs) {
      const ids = fileRefs.map((f) => f.file_id).filter(Boolean);
      if (!ids.length) {
        return;
      }
      this.$api.afterLogin(() => {
        this.$api.post('files/get', ids).then((res) => {
          const list = res.data.data || [];
          list.forEach((item) => {
            const ref = fileRefs.find((r) => r.file_id === item.id);
            if (ref && ref.name) {
              item.name = ref.name;
            }
          });
          this.savedFiles = list;
        });
      });
    },
    removeFile(index) {
      const all = this.displayFiles;
      const target = all[index];
      if (!target) {
        return;
      }
      const savedIdx = this.savedFiles.indexOf(target);
      if (savedIdx >= 0) {
        this.savedFiles.splice(savedIdx, 1);
      } else {
        const pendingIdx = this.pendingFiles.indexOf(target);
        if (pendingIdx >= 0) {
          this.pendingFiles.splice(pendingIdx, 1);
        }
      }
      this.emitChange();
    },
  },
};
</script>
